import React, { useState, useEffect, useRef, useCallback } from 'react';
import { LogEntry, LogDetails } from '../types';
import { wsService } from '../services/websocket';
import { logsAPI, photosAPI } from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import './LogsViewer.css';

// Кеш для аватаров пользователей
const avatarCache = new Map<string, { url: string; timestamp: number }>();
// Кеш для пользователей без аватаров (чтобы не загружать повторно)
const noAvatarCache = new Set<string>();
// Кеш для отслеживания загружающихся аватаров (предотвращает дублирование запросов)
const loadingCache = new Map<string, Promise<string | null>>();
const AVATAR_CACHE_DURATION = 5 * 60 * 1000; // 5 минут

const LogsViewer: React.FC = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [selectedLog, setSelectedLog] = useState<LogDetails | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [userPhotos, setUserPhotos] = useState<any[]>([]);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [isLoadingLogDetails, setIsLoadingLogDetails] = useState(false);
  const [isLoadingPhotos, setIsLoadingPhotos] = useState(false);
  const [isWebSocketConnected, setIsWebSocketConnected] = useState(false);
  const [logsLoadingProgress, setLogsLoadingProgress] = useState({ loaded: 0, total: 0 });
  const [isLoadingInitialLogs, setIsLoadingInitialLogs] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [autoScroll, setAutoScroll] = useState(true);
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [, setAvatarUrls] = useState<Map<string, string>>(new Map());
  const [visibleLogs, setVisibleLogs] = useState<Set<string>>(new Set());
  const logContainerRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Функция для предзагрузки аватаров только для последних N логов
  const preloadAvatars = useCallback(async (logs: LogEntry[], limit: number = 20) => {
    const uniqueChatIds = new Set<string>();
    
    // Берем только последние N логов (самые новые)
    const recentLogs = logs.slice(-limit);
    
    // Собираем уникальные chatId из последних логов
    recentLogs.forEach(log => {
      if (isUserAction(log) && log.chatId) {
        const cacheKey = String(log.chatId);
        // Проверяем, не загружается ли уже аватар или не загружен ли он
        if (!avatarCache.has(cacheKey) && !noAvatarCache.has(cacheKey) && !loadingCache.has(cacheKey)) {
          uniqueChatIds.add(cacheKey);
        }
      }
    });
    
    // Ограничиваем количество уникальных пользователей
    const limitedChatIds = Array.from(uniqueChatIds).slice(0, 20);
    
    // Загружаем аватары для ограниченного количества пользователей
    const loadPromises = limitedChatIds.map(chatId => 
      getCachedAvatar(chatId).catch(() => null)
    );
    
    await Promise.allSettled(loadPromises);
  }, []);

  const loadInitialLogs = useCallback(async () => {
    try {
      setIsLoading(true);
      setIsLoadingInitialLogs(true);
      setLogsLoadingProgress({ loaded: 0, total: 0 });
      
      const response = await logsAPI.getLogs(1, 50);
      if (response.success) {
        // API уже возвращает логи в правильном порядке (новые сверху, старые снизу)
        // Переворачиваем массив, чтобы новые логи были снизу
        const reversedLogs = response.logs.reverse();
        setLogs(reversedLogs);
        setHasMore(response.pagination.page < response.pagination.totalPages);
        setCurrentPage(1);
        setLogsLoadingProgress({ loaded: response.logs.length, total: response.pagination.total });
        
        // Предзагружаем аватары для загруженных логов
        preloadAvatars(reversedLogs);
      }
    } catch (error) {
      console.error('Failed to load logs:', error);
    } finally {
      setIsLoading(false);
      setIsLoadingInitialLogs(false);
    }
  }, [preloadAvatars]);

  const loadMoreLogs = async () => {
    if (isLoadingMore || !hasMore) return;

    try {
      setIsLoadingMore(true);
      const response = await logsAPI.getLogs(currentPage + 1, 50);
      if (response.success && response.logs.length > 0) {
        setLogs(prev => {
          // Добавляем старые логи в начало массива (они должны быть сверху)
          // API уже возвращает логи в правильном порядке
          const combined = [...response.logs, ...prev];
          
          // Предзагружаем аватары для новых логов
          preloadAvatars(response.logs);
          
          return combined;
        });
        setCurrentPage(prev => prev + 1);
        setHasMore(response.pagination.page < response.pagination.totalPages);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Failed to load more logs:', error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const setupWebSocket = useCallback(async () => {
    try {
      await wsService.connect();
      setIsWebSocketConnected(true);

      wsService.on('existing_logs', (data: any) => {
        if (data.logs && Array.isArray(data.logs)) {
          if (data.isLastBatch) {
            // Это последний батч, заменяем все логи
            // API уже возвращает логи в правильном порядке (новые сверху, старые снизу)
            // Переворачиваем массив, чтобы новые логи были снизу
            const reversedLogs = data.logs.reverse();
            setLogs(reversedLogs);
            setIsLoadingInitialLogs(false);
            
            // Предзагружаем аватары для загруженных логов
            preloadAvatars(reversedLogs);
            // Прокручиваем к низу после загрузки всех логов
            setTimeout(() => {
              if (logContainerRef.current) {
                logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
              }
            }, 100);
          } else {
            // Добавляем логи к существующим
            setLogs(prev => {
              // API уже возвращает логи в правильном порядке, просто добавляем их
              const combined = [...data.logs, ...prev];
              
              // Предзагружаем аватары для новых логов
              preloadAvatars(data.logs);
              
              return combined;
            });
          }
          
          // Обновляем прогресс загрузки
          if (data.totalLogs && data.loadedLogs) {
            setLogsLoadingProgress({ 
              loaded: data.loadedLogs, 
              total: data.totalLogs 
            });
          }
        }
      });

      wsService.on('new_log', (newLog: LogEntry) => {
        setLogs(prev => {
          // Добавляем новый лог в конец массива (новые логи должны быть снизу)
          const updated = [...prev, newLog];
          
          // Предзагружаем аватар для нового лога
          if (isUserAction(newLog) && newLog.chatId) {
            preloadAvatars([newLog]);
          }
          
          return updated;
        });
        // Обновляем счетчик загруженных логов
        setLogsLoadingProgress(prev => ({
          ...prev,
          loaded: prev.loaded + 1
        }));
      });

      wsService.on('logs_error', (error: any) => {
        console.error('WebSocket logs error:', error);
        setIsLoadingInitialLogs(false);
      });

      wsService.on('status', (status: string) => {
        console.log('Bot status:', status);
      });

      wsService.on('unauthorized', () => {
        console.log('WebSocket unauthorized, redirecting to login');
        setIsWebSocketConnected(false);
        // Перенаправление уже обработано в WebSocket сервисе
      });

      wsService.on('disconnect', () => {
        setIsWebSocketConnected(false);
      });

    } catch (error) {
      console.error('Failed to setup WebSocket:', error);
      setIsWebSocketConnected(false);
    }
  }, [preloadAvatars]);

  useEffect(() => {
    loadInitialLogs();
    setupWebSocket();
    setupIntersectionObserver();

    return () => {
      wsService.disconnect();
      // Очищаем подписки на события
      wsService.off('existing_logs', () => {});
      wsService.off('new_log', () => {});
      wsService.off('status', () => {});
      wsService.off('unauthorized', () => {});
      
      // Очищаем кеш аватаров при размонтировании
      avatarCache.clear();
      noAvatarCache.clear();
      loadingCache.clear();
      
      // Очищаем Intersection Observer
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loadInitialLogs, setupWebSocket]);

  // Автоматическое прокручивание к низу при добавлении новых логов
  useEffect(() => {
    if (logContainerRef.current && logs.length > 0 && autoScroll) {
      const container = logContainerRef.current;
      // Прокручиваем к низу только если пользователь уже был внизу
      const isAtBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 10;
      if (isAtBottom) {
        container.scrollTop = container.scrollHeight;
      }
    }
  }, [logs, autoScroll]);

  // Прокручивание к низу при первом загрузке логов
  useEffect(() => {
    if (logContainerRef.current && logs.length > 0 && !isLoading && autoScroll) {
      const container = logContainerRef.current;
      // Небольшая задержка для корректного отображения
      setTimeout(() => {
        container.scrollTop = container.scrollHeight;
      }, 100);
    }
  }, [isLoading, logs.length, autoScroll]);

  const handleLogClick = async (log: LogEntry) => {
    try {
      setIsLoadingLogDetails(true);
      setShowModal(true);
      
      const logDetails = await logsAPI.getLogDetails(log._id);
      setSelectedLog(logDetails);

      // Загружаем фотографии пользователя, если есть chatId
          if (logDetails.chatId && String(logDetails.chatId) !== "unknown") {
        try {
          setIsLoadingPhotos(true);
          // Преобразуем chatId в число, если это возможно
          const chatId = typeof logDetails.chatId === 'string' ? parseInt(logDetails.chatId) : logDetails.chatId;
          
          if (!isNaN(chatId)) {
            const photosResponse = await photosAPI.getUserPhotos(chatId);
            if (photosResponse.success) {
              setUserPhotos(photosResponse.photos || []);
              setCurrentPhotoIndex(0);
              if (photosResponse.message) {
                console.log('Photos API message:', photosResponse.message);
              }
            } else {
              console.error('Photos API error: No photos found');
              setUserPhotos([]);
            }
          } else {
            console.warn('Invalid chatId for photos:', logDetails.chatId);
            setUserPhotos([]);
          }
        } catch (error) {
          console.error('Failed to load user photos:', error);
          setUserPhotos([]);
        } finally {
          setIsLoadingPhotos(false);
        }
      } else {
        setUserPhotos([]);
      }
    } catch (error) {
      console.error('Failed to load log details:', error);
      setShowModal(false);
    } finally {
      setIsLoadingLogDetails(false);
    }
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop } = e.currentTarget;
    // Загружаем старые логи когда прокручиваем вверх (к началу списка)
    if (scrollTop === 0 && !isLoadingMore && hasMore) {
      loadMoreLogs();
    }
  };

  const nextPhoto = () => {
    if (userPhotos.length > 0) {
      setCurrentPhotoIndex(prev => (prev + 1) % userPhotos.length);
    }
  };

  const prevPhoto = useCallback(() => {
    if (userPhotos.length > 0) {
      setCurrentPhotoIndex(prev => (prev - 1 + userPhotos.length) % userPhotos.length);
    }
  }, [userPhotos.length]);

  // Використовуємо функцію для уникнення попередження ESLint
  React.useEffect(() => {
    // Функція prevPhoto готова до використання
  }, [prevPhoto]);

  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp);
    const pad = (num: number) => (num < 10 ? '0' + num : num);
    
    const day = pad(date.getDate());
    const month = pad(date.getMonth() + 1);
    const year = date.getFullYear();
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());
    
    return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
  };

  const getLogTypeIcon = (logType: string) => {
    switch (logType?.toLowerCase()) {
      case 'error':
        return '❌';
      case 'warning':
        return '⚠️';
      case 'info':
        return 'ℹ️';
      case 'success':
        return '✅';
      case 'debug':
        return '🔍';
      default:
        return '📝';
    }
  };

  const getLogTypeColor = (logType: string) => {
    switch (logType?.toLowerCase()) {
      case 'error':
        return 'var(--danger)';
      case 'warning':
        return 'var(--warning)';
      case 'info':
        return 'var(--info)';
      case 'success':
        return 'var(--success)';
      case 'debug':
        return 'var(--text-muted)';
      default:
        return 'var(--text-secondary)';
    }
  };

  const isUserAction = (log: LogEntry) => {
    // Определяем, является ли лог действием пользователя
    // Пользовательские действия обычно имеют userName и command
    return log.userName && log.userName !== 'unknown' && log.command && log.command !== 'unknown';
  };

  // Функция для получения аватара из кеша или загрузки
  const getCachedAvatar = async (chatId: string | number, userName?: string): Promise<string | null> => {
    const cacheKey = String(chatId);
    const now = Date.now();
    
    // Проверяем кеш аватаров
    const cached = avatarCache.get(cacheKey);
    if (cached && (now - cached.timestamp) < AVATAR_CACHE_DURATION) {
      return cached.url;
    }
    
    // Проверяем кеш пользователей без аватаров
    if (noAvatarCache.has(cacheKey)) {
      return null;
    }
    
    // Проверяем, не загружается ли уже аватар для этого пользователя
    if (loadingCache.has(cacheKey)) {
      return loadingCache.get(cacheKey)!;
    }
    
    // Создаем промис для загрузки аватара
    const loadPromise = (async () => {
      try {
        const numericChatId = typeof chatId === 'string' ? parseInt(chatId, 10) : chatId;
        if (isNaN(numericChatId)) return null;
        
        const response = await photosAPI.getUserPhotos(numericChatId);
        if (response.success && response.photos && response.photos.length > 0) {
          const baseURL = process.env.REACT_APP_API_URL || 'https://api.denisphones.org';
          const avatarUrl = `${baseURL}${response.photos[0].url}`;
          
          // Сохраняем в кеш аватаров
          avatarCache.set(cacheKey, { url: avatarUrl, timestamp: now });
          
          // Обновляем состояние
          setAvatarUrls(prev => new Map(prev.set(cacheKey, avatarUrl)));
          
          return avatarUrl;
        } else {
          // Пользователь не имеет аватара - добавляем в кеш отсутствующих
          noAvatarCache.add(cacheKey);
          return null;
        }
      } catch (error) {
        console.error('Ошибка загрузки аватара:', error);
        // При ошибке тоже добавляем в кеш отсутствующих, чтобы не повторять запросы
        noAvatarCache.add(cacheKey);
        return null;
      } finally {
        // Удаляем из кеша загрузки после завершения
        loadingCache.delete(cacheKey);
      }
    })();
    
    // Сохраняем промис в кеше загрузки
    loadingCache.set(cacheKey, loadPromise);
    
    return loadPromise;
  };

  // Настройка Intersection Observer для ленивой загрузки аватаров
  const setupIntersectionObserver = () => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const logId = entry.target.getAttribute('data-log-id');
            const chatId = entry.target.getAttribute('data-chat-id');
            
            if (logId && chatId) {
              // Проверяем, нужно ли загружать аватар
              const cacheKey = String(chatId);
              const cached = avatarCache.get(cacheKey);
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const now = Date.now();
              
              // Загружаем только если аватар не в кеше, не в кеше отсутствующих и не истек срок
              if (!cached && !noAvatarCache.has(cacheKey)) {
                setVisibleLogs(prev => new Set(prev.add(logId)));
              }
            }
          }
        });
      },
      {
        root: logContainerRef.current,
        rootMargin: '100px', // Загружаем аватары за 100px до появления в зоне видимости
        threshold: 0.1
      }
    );
  };

  // Функция для загрузки аватара при появлении в зоне видимости
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const loadAvatarOnVisible = async (log: LogEntry) => {
    if (isUserAction(log) && log.chatId) {
      const cacheKey = String(log.chatId);
      if (!avatarCache.has(cacheKey) && !noAvatarCache.has(cacheKey)) {
        await getCachedAvatar(log.chatId, log.userName);
      }
    }
  };

  // Компонент для кешированного аватара с ленивой загрузкой
  const CachedUserAvatar: React.FC<{ 
    chatId: string | number; 
    userName?: string; 
    size?: string; 
    className?: string;
    logId?: string;
    isVisible?: boolean;
  }> = ({ 
    chatId, 
    userName, 
    size = "large", 
    className = "",
    logId,
    isVisible = false
  }) => {
    const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);

    useEffect(() => {
      const loadAvatar = async () => {
        const cacheKey = String(chatId);
        const now = Date.now();
        
        // Проверяем кеш аватаров
        const cached = avatarCache.get(cacheKey);
        if (cached && (now - cached.timestamp) < AVATAR_CACHE_DURATION) {
          setAvatarUrl(cached.url);
          setLoading(false);
          setHasLoaded(true);
          return;
        }
        
        // Проверяем кеш пользователей без аватаров
        if (noAvatarCache.has(cacheKey)) {
          setError(true);
          setLoading(false);
          setHasLoaded(true);
          return;
        }
        
        // Загружаем только если элемент видим и аватар еще не загружался
        if (!isVisible || hasLoaded) {
          setLoading(false);
          return;
        }
        
        // Используем общую функцию загрузки аватаров для предотвращения дублирования
        try {
          setLoading(true);
          setError(false);
          
          const avatarUrl = await getCachedAvatar(chatId, userName);
          if (avatarUrl) {
            setAvatarUrl(avatarUrl);
          } else {
            setError(true);
          }
        } catch (err) {
          console.error('Ошибка загрузки аватара:', err);
          setError(true);
        } finally {
          setLoading(false);
          setHasLoaded(true);
        }
      };

      if (chatId) {
        loadAvatar();
      }
    }, [chatId, isVisible, hasLoaded, userName]);

    const getInitials = () => {
      if (userName) {
        return userName.charAt(0).toUpperCase();
      }
      return '?';
    };

    const getSizeClass = () => {
      switch (size) {
        case 'tiny': return 'avatar-tiny';
        case 'small': return 'avatar-small';
        case 'large': return 'avatar-large';
        case 'extra-large': return 'avatar-extra-large';
        default: return 'avatar-medium';
      }
    };

    const avatarClasses = `user-avatar ${getSizeClass()} ${className}`;

    if (loading) {
      return (
        <div className={`${avatarClasses} avatar-loading`}>
          <div className="avatar-spinner"></div>
        </div>
      );
    }

    if (error || !avatarUrl) {
      return (
        <div className={`${avatarClasses} avatar-fallback`}>
          {getInitials()}
        </div>
      );
    }

    return (
      <div className={`${avatarClasses} avatar-image`}>
        <img 
          src={avatarUrl} 
          alt={`Аватар ${userName || 'пользователя'}`}
          onError={() => setError(true)}
          loading="lazy"
        />
      </div>
    );
  };

  const truncateMessage = (message: string, maxLength: number = 150) => {
    if (message.length <= maxLength) return message;
    return message.substring(0, maxLength) + '...';
  };

  const filteredLogs = logs.filter(log => {
    const matchesSearch = searchTerm === '' || 
      log.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.command?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterType === 'all' || log.logtype === filterType;
    
    return matchesSearch && matchesFilter;
  });

  const toggleAutoScroll = () => {
    setAutoScroll(!autoScroll);
  };

  const scrollToBottom = () => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  };

  const scrollToTop = () => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = 0;
    }
  };

  const formatValue = (value: any): string => {
    if (value === null) return 'Не указано';
    if (value === undefined) return 'Не определено';
    
    if (Array.isArray(value)) {
      return value.map(item => {
        if (typeof item === 'object') {
          return JSON.stringify(item, null, 2);
        }
        return item;
      }).join(', ');
    }
    
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }
    
    return value.toString();
  };

  if (isLoading) {
    return (
      <div className="logs-viewer">
        <div className="logs-header">
          <h3>Логи системы</h3>
          <div className="connection-status">
            <span className={`status-indicator ${isWebSocketConnected ? 'connected' : 'disconnected'}`}>
              {isWebSocketConnected ? '🟢 Подключено' : '🔴 Отключено'}
            </span>
          </div>
        </div>
        <LoadingSpinner text="Загрузка логов..." />
        {isLoadingInitialLogs && logsLoadingProgress.total > 0 && (
          <div className="loading-progress">
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ 
                  width: `${(logsLoadingProgress.loaded / logsLoadingProgress.total) * 100}%` 
                }}
              />
            </div>
            <div className="progress-text">
              Загружено {logsLoadingProgress.loaded} из {logsLoadingProgress.total} логов
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="logs-viewer">
      <div className="logs-header">
        <div className="logs-title-section">
          <h3>Логи системы</h3>
          <div className="logs-stats">
            <span className="logs-count">Всего: {logs.length}</span>
            <span className="filtered-count">Показано: {filteredLogs.length}</span>
          </div>
        </div>
        <div className="logs-controls">
          <div className="connection-status">
            <span className={`status-indicator ${isWebSocketConnected ? 'connected' : 'disconnected'}`}>
              {isWebSocketConnected ? '🟢 Подключено' : '🔴 Отключено'}
            </span>
          </div>
          <div className="logs-actions">
            <button 
              className={`control-btn ${autoScroll ? 'active' : ''}`}
              onClick={toggleAutoScroll}
              title={autoScroll ? 'Отключить автопрокрутку' : 'Включить автопрокрутку'}
            >
              {autoScroll ? '⏸️' : '▶️'}
            </button>
            <button 
              className="control-btn"
              onClick={scrollToBottom}
              title="Прокрутить вниз"
            >
              ⬇️
            </button>
            <button 
              className="control-btn"
              onClick={scrollToTop}
              title="Прокрутить вверх"
            >
              ⬆️
            </button>
            <button 
              className={`control-btn ${showSearchBar ? 'active' : ''}`}
              onClick={() => setShowSearchBar(!showSearchBar)}
              title="Поиск и фильтры"
            >
              🔍
            </button>
          </div>
        </div>
      </div>
      
      {showSearchBar && (
        <div className="logs-filters">
          <div className="search-bar">
            <input
              type="text"
              placeholder="Поиск по сообщению, пользователю или команде..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          <div className="filter-controls">
            <select 
              value={filterType} 
              onChange={(e) => setFilterType(e.target.value)}
              className="filter-select"
            >
              <option value="all">Все типы</option>
              <option value="info">Информация</option>
              <option value="warning">Предупреждение</option>
              <option value="error">Ошибка</option>
              <option value="success">Успех</option>
              <option value="debug">Отладка</option>
            </select>
            <button 
              className="clear-filters-btn"
              onClick={() => {
                setSearchTerm('');
                setFilterType('all');
              }}
            >
              Очистить
            </button>
          </div>
        </div>
      )}
      
      <div 
        className="logs-container"
        ref={logContainerRef}
        onScroll={handleScroll}
      >
        {isLoadingMore && (
          <div className="loading-more">
            <LoadingSpinner size="small" text="Загрузка предыдущих логов..." />
          </div>
        )}
        
        {filteredLogs.map((log) => (
          <div 
            key={log._id} 
            className="log-entry" 
            onClick={() => handleLogClick(log)}
            data-log-id={log._id}
            data-chat-id={log.chatId || ''}
            ref={(el) => {
              if (el && observerRef.current) {
                observerRef.current.observe(el);
              }
            }}
          >
            <div className="log-entry-header">
              <div className="log-type-badge" style={{ color: getLogTypeColor(log.logtype || 'info') }}>
                {isUserAction(log) && log.chatId ? (
                  <CachedUserAvatar 
                    chatId={log.chatId} 
                    userName={log.userName} 
                    size="large"
                    className="log-avatar"
                    logId={log._id}
                    isVisible={visibleLogs.has(log._id)}
                  />
                ) : (
                  <span className="log-type-icon">{getLogTypeIcon(log.logtype || 'info')}</span>
                )}
                <span className="log-type-text">{log.logtype || 'info'}</span>
              </div>
              <div className="log-header-info">
                {log.userName && <span className="user-info-header">👤 {log.userName}</span>}
                {log.command && <span className="command-info-header">⚡ {log.command}</span>}
              </div>
              <div className="log-timestamp">{log.timestamp ? formatTimestamp(log.timestamp) : 'Нет времени'}</div>
            </div>
            <div className="log-content">
              <div className="log-message">{truncateMessage(log.message)}</div>
              <div className="log-meta">
                {log.chatId && log.chatId !== 'unknown' && <span className="chat-info">💬 Chat: {log.chatId}</span>}
              </div>
            </div>
            <div className="log-actions">
              <button className="details-button" onClick={(e) => {
                e.stopPropagation();
                handleLogClick(log);
              }}>
                Подробнее
              </button>
            </div>
          </div>
        ))}
        
        {filteredLogs.length === 0 && logs.length > 0 && (
          <div className="no-logs">
            <div className="no-logs-icon">🔍</div>
            <div className="no-logs-text">Нет логов, соответствующих фильтрам</div>
            <button 
              className="clear-filters-btn"
              onClick={() => {
                setSearchTerm('');
                setFilterType('all');
              }}
            >
              Очистить фильтры
            </button>
          </div>
        )}
        
        {logs.length === 0 && (
          <div className="no-logs">
            <div className="no-logs-icon">📝</div>
            <div className="no-logs-text">Нет логов для отображения</div>
          </div>
        )}
      </div>

      {/* Модальное окно с деталями лога */}
      {showModal && selectedLog && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Детали лога #{selectedLog._id.slice(-6)}</h3>
              <button onClick={() => setShowModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              {isLoadingLogDetails ? (
                <LoadingSpinner text="Загрузка деталей лога..." />
              ) : (
                <div className="log-details">
                  {/* Фотографии пользователя */}
                  {isLoadingPhotos ? (
                    <div className="user-photos">
                      <LoadingSpinner size="small" text="Загрузка фотографий..." />
                    </div>
                  ) : userPhotos.length > 0 ? (
                    <div className="user-photos">
                      <div className="photo-progress">
                        {userPhotos.map((_, index) => (
                          <div 
                            key={index} 
                            className={`photo-progress-segment ${index <= currentPhotoIndex ? 'active' : ''}`}
                          />
                        ))}
                      </div>
                      
                      <div className="photo-counter">
                        {currentPhotoIndex + 1}/{userPhotos.length}
                      </div>
                      
                      {userPhotos.map((photo, index) => {
                        // Формируем полный URL для изображения
                        const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://api.denisphones.org';
                        const fullPhotoUrl = photo.url.startsWith('http') ? photo.url : `${apiBaseUrl}${photo.url}`;
                        
                        return (
                          <div 
                            key={index} 
                            className={`user-photo ${index === currentPhotoIndex ? 'active' : ''}`}
                          >
                            <img 
                              src={fullPhotoUrl} 
                              alt="User profile" 
                              onClick={nextPhoto}
                              onError={(e) => {
                                console.error('Failed to load photo:', fullPhotoUrl);
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          </div>
                        );
                      })}
                    </div>
                  ) : selectedLog.chatId && String(selectedLog.chatId) !== "unknown" ? (
                    <div className="user-photos no-photos">
                      <div className="no-photos-message">
                        📷 Фотографии профиля недоступны
                      </div>
                    </div>
                  ) : null}
                
                {/* Метаданные лога */}
                <div className="log-metadata">
                  <div className="log-type-header">
                  <div className="log-type-badge-large" style={{ backgroundColor: getLogTypeColor(selectedLog.logtype || 'info') }}>
                    <span className="log-type-icon-large">{getLogTypeIcon(selectedLog.logtype || 'info')}</span>
                    <span className="log-type-text-large">{(selectedLog.logtype || 'info').toUpperCase()}</span>
                  </div>
                  <div className="log-id-display">#{selectedLog._id.slice(-8)}</div>
                </div>
                  
                  <div className="metadata-grid">
                    <div className="metadata-item">
                      <span className="metadata-label">ID лога</span>
                      <span className="metadata-value">{selectedLog._id}</span>
                    </div>
                    
                    <div className="metadata-item">
                      <span className="metadata-label">Тип лога</span>
                      <span className="metadata-value">{selectedLog.logtype}</span>
                    </div>
                    
                    <div className="metadata-item">
                      <span className="metadata-label">Команда</span>
                      <span className="metadata-value">{selectedLog.command}</span>
                    </div>
                    
                    <div className="metadata-item">
                      <span className="metadata-label">Время</span>
                      <span className="metadata-value">{formatTimestamp(selectedLog.timestamp)}</span>
                    </div>
                    
                    <div className="metadata-item">
                      <span className="metadata-label">ID чата</span>
                      <span className="metadata-value">{selectedLog.chatId}</span>
                    </div>
                    
                    <div className="metadata-item">
                      <span className="metadata-label">Тип чата</span>
                      <span className="metadata-value">{selectedLog.chatType}</span>
                    </div>
                    
                    <div className="metadata-item">
                      <span className="metadata-label">ID пользователя</span>
                      <span className="metadata-value">{selectedLog.userId}</span>
                    </div>
                    
                    <div className="metadata-item">
                      <span className="metadata-label">Имя пользователя</span>
                      <span className="metadata-value">{selectedLog.userName}</span>
                    </div>
                    
                    <div className="metadata-item">
                      <span className="metadata-label">Имя</span>
                      <span className="metadata-value">{selectedLog.firstName}</span>
                    </div>
                    
                    <div className="metadata-item">
                      <span className="metadata-label">Фамилия</span>
                      <span className="metadata-value">{selectedLog.lastName}</span>
                    </div>
                    
                    <div className="metadata-item">
                      <span className="metadata-label">Название чата</span>
                      <span className="metadata-value">{selectedLog.chatTitle || 'Не указано'}</span>
                    </div>
                    
                    {selectedLog.extraData && (
                      <div className="metadata-item complex">
                        <span className="metadata-label">Дополнительные данные</span>
                        <span className="metadata-value complex">
                          {formatValue(selectedLog.extraData)}
                        </span>
                      </div>
                    )}
                    
                    {selectedLog.errMsg && (
                      <div className="metadata-item">
                        <span className="metadata-label">Ошибка</span>
                        <span className="metadata-value">{selectedLog.errMsg}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                  {/* Полное содержимое лога */}
                  <div className="log-full-content">
                    <pre>{JSON.stringify(selectedLog, null, 2)}</pre>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LogsViewer;
